import { useEffect, useRef } from 'react';
import {
  WSAnyMessage,
  WSEventTypeIds,
  WSMessageEventDetail,
  WSSalesPipelineData,
  WSTicketData,
  WSTicketMessageData,
} from '@/shared/features/websockets/types/websockets';
import { askNotificationPermission } from '@/hooks/notifications/notifications';
import { MESSENGER_CONFIG } from '@/config/messenger';
import { useAuth } from '@/store/auth/authHooks';
import { usePreferences } from '@/hooks/preferences';
import { DEFAULT_NOTIFICATION_SOUND } from '@/config/constants';
import { useInvalidateTags } from '@/store/useInvalidateTags';

const NOTIFICATION_CLAIM_PREFIX = 'ws-notification-claim:';

/*
 * All tabs normally receive the websocket event within a few milliseconds.
 *
 * During this period they compete for ownership of this particular event.
 */
const NOTIFICATION_ELECTION_DELAY = 120;

/*
 * Keep completed claims for a while so that if the websocket itself
 * delivers the same message again shortly afterwards, it is ignored.
 */
const NOTIFICATION_CLAIM_TTL = 10_000;

type NotificationClaim = {
  token: string;
  createdAt: number;
  status: 'candidate' | 'done';
};

const createToken = () => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}-${Math.random()}`;
};

/*
 * We need exactly the same key in all tabs for exactly the same
 * websocket payload.
 */
const hashString = (value: string) => {
  let hash = 2166136261;

  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
};

const getClaim = (key: string): NotificationClaim | null => {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as NotificationClaim;

    if (typeof parsed?.token !== 'string' || typeof parsed?.createdAt !== 'number' || !['candidate', 'done'].includes(parsed?.status)) {
      localStorage.removeItem(key);

      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(key);

    return null;
  }
};

const cleanupOldNotificationClaims = () => {
  const now = Date.now();

  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);

    if (!key?.startsWith(NOTIFICATION_CLAIM_PREFIX)) {
      continue;
    }

    const claim = getClaim(key);

    if (!claim) {
      continue;
    }

    if (now - claim.createdAt > NOTIFICATION_CLAIM_TTL) {
      localStorage.removeItem(key);
    }
  }
};

/*
 * All tabs which want to show this websocket event execute this function.
 *
 * Example:
 *
 * TAB 1 writes token A
 * TAB 2 writes token B
 * TAB 3 writes token C
 *
 * After 120 ms:
 *
 * TAB 1 reads token C -> loses
 * TAB 2 reads token C -> loses
 * TAB 3 reads token C -> wins
 *
 * Only TAB 3 runs callback().
 */
const runNotificationOnceAcrossTabs = async (websocketPayload: string, callback: () => void) => {
  cleanupOldNotificationClaims();

  const key = `${NOTIFICATION_CLAIM_PREFIX}${hashString(websocketPayload)}`;

  const existingClaim = getClaim(key);

  /*
   * Somebody has already completely processed this event.
   */
  if (existingClaim?.status === 'done' && Date.now() - existingClaim.createdAt < NOTIFICATION_CLAIM_TTL) {
    return;
  }

  const token = createToken();

  /*
   * Even if another tab has a "candidate" claim, overwrite it.
   *
   * That is intentional: every tab receiving the event participates
   * in the election.
   */
  const claim: NotificationClaim = {
    token,
    createdAt: Date.now(),
    status: 'candidate',
  };

  localStorage.setItem(key, JSON.stringify(claim));

  /*
   * Give the other tabs enough time to receive the same websocket
   * event and submit their own candidate.
   */
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, NOTIFICATION_ELECTION_DELAY);
  });

  const winner = getClaim(key);

  /*
   * Another tab overwrote our token.
   */
  if (!winner || winner.token !== token) {
    return;
  }

  /*
   * Mark it completed BEFORE Notification/Audio.
   *
   * A tab receiving the same websocket event slightly later will now
   * immediately see "done" and will not generate another notification.
   */
  const completedClaim: NotificationClaim = {
    token,
    createdAt: Date.now(),
    status: 'done',
  };

  localStorage.setItem(key, JSON.stringify(completedClaim));

  try {
    callback();
  } catch (err) {
    /*
     * If notification creation itself fails synchronously,
     * remove our claim so a later websocket delivery can retry.
     */
    const currentClaim = getClaim(key);

    if (currentClaim?.token === token) {
      localStorage.removeItem(key);
    }

    throw err;
  }
};

const activeAudios = new Set<HTMLAudioElement>();

const showNotification = ({
  title,
  body,
  url,
  sound,
  volume,
}: {
  title: string;
  body: string;
  url: string;
  sound: string;
  volume: number;
}) => {
  const image = `${window.location.origin}/img/favicon.png`;

  /*
   * Browser notification.
   */
  const notification = new Notification(title, {
    body,
    icon: image,
  });

  notification.onclick = (event) => {
    event.preventDefault();

    window.open(`${window.location.origin}${url}`, '_blank');
  };

  /*
   * Sound notification.
   */
  const audio = new Audio(`/sound/notifications/${sound || DEFAULT_NOTIFICATION_SOUND}`);

  audio.volume = volume;
  audio.muted = false;
  audio.preload = 'auto';

  /*
   * Keep a strong reference to the Audio instance until it finishes.
   */
  activeAudios.add(audio);

  const cleanup = () => {
    activeAudios.delete(audio);
  };

  audio.addEventListener('ended', cleanup, {
    once: true,
  });

  audio.addEventListener('error', cleanup, {
    once: true,
  });

  audio.play().catch((err) => {
    cleanup();

    console.error('Unable to play notification sound:', err);
  });
};

export const useMessageNotifications = () => {
  const { user } = useAuth();

  const { preferences } = usePreferences();

  const refetchPipelines = useInvalidateTags(['SalesPipeline'], false);

  const refetchOrders = useInvalidateTags(['Order', 'OrderSnapshot'], false);

  const refetchRevisions = useInvalidateTags(['Revision'], false);

  const prefs = preferences?.notificationSoundPreferences;

  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;

  useEffect(() => {
    const handleNotification = async (event: CustomEvent<WSMessageEventDetail>) => {
      /*
       * Ignore messages from other websocket channels.
       */
      if (event.detail.path !== MESSENGER_CONFIG.websockets.ticket) {
        return;
      }

      const websocketPayload = event.detail.data.data as string;

      let eventData: WSAnyMessage;

      try {
        eventData = JSON.parse(websocketPayload);
      } catch (err) {
        console.error('Unable to parse websocket message:', err);

        return;
      }

      const { idEventType } = eventData.data;

      let title = '';
      let body = '';
      let url = '';
      let sound = '';

      if ([WSEventTypeIds.TicketMessageCreatedByWriter, WSEventTypeIds.TicketMessageCreatedByCustomer].includes(idEventType)) {
        const data = eventData.data as WSTicketMessageData;

        /*
         * Current user generated the message.
         */
        if (data?.author?.id === userIdRef.current) {
          return;
        }

        title = 'New message received.';
        body = 'Check message';

        url = `/tickets/${data.idTicket}?tab=messenger`;

        if (idEventType === WSEventTypeIds.TicketMessageCreatedByWriter) {
          sound = prefsRef.current?.ticketMessageReceivedFromWriter || '';
        }

        if (idEventType === WSEventTypeIds.TicketMessageCreatedByCustomer) {
          sound = prefsRef.current?.ticketMessageReceivedFromCustomer || '';
        }
      } else if ([WSEventTypeIds.TicketCreatedByWriter, WSEventTypeIds.TicketCreatedByCustomer].includes(idEventType)) {
        const ticketData = eventData.data as WSTicketData;

        if (ticketData?.author?.id === userIdRef.current) {
          return;
        }

        title = 'New ticket appeared.';
        body = 'Check ticket';
        url = '/todos/tickets?tab=pending';

        if (idEventType === WSEventTypeIds.TicketCreatedByWriter) {
          sound = prefsRef.current?.ticketCreatedByWriter || '';
        }

        if (idEventType === WSEventTypeIds.TicketCreatedByCustomer) {
          sound = prefsRef.current?.ticketCreatedByCustomer || '';
        }
      } else if (
        [WSEventTypeIds.RevisionCreated, WSEventTypeIds.RevisionCreatedByCustomer, WSEventTypeIds.RevisionCreatedByUser].includes(
          idEventType,
        )
      ) {
        const revisionData = eventData.data as WSTicketData;

        if (revisionData?.author?.id === userIdRef.current) {
          return;
        }

        title = 'New revision requested.';
        body = 'Check revision';

        url = '/todos/revisions?tab=claimed';

        sound = prefsRef.current?.revisionRequested || '';
      } else if (idEventType === WSEventTypeIds.RevisionToggleVerificationStatusByUser) {
        const revisionData = eventData.data as WSTicketData;

        if (revisionData?.author?.id === userIdRef.current) {
          return;
        }

        title = 'Revision verification status changed.';

        body = 'Check revision';

        url = `/revisions/${revisionData.id}`;

        sound = prefsRef.current?.orderVerified || '';
      } else if ([WSEventTypeIds.OrderPaid, WSEventTypeIds.OrderPaymentReceived].includes(idEventType)) {
        const orderData = eventData.data as WSTicketData;

        if (orderData?.author?.id === userIdRef.current) {
          return;
        }

        title = 'New order paid.';
        body = 'Check order';

        url = '/todos/orders?tab=pending';

        sound = prefsRef.current?.orderPaid || '';
      } else if (idEventType === WSEventTypeIds.OrderToggleVerificationStatusByUser) {
        const orderData = eventData.data as WSTicketData;

        const orderPath = `/orders/${orderData.id}`;

        /*
         * RTK Query invalidation happens independently in every tab.
         */
        refetchOrders();

        /*
         * This particular tab doesn't need a popup because
         * it already shows the order.
         *
         * IMPORTANT:
         * It simply doesn't participate in the election.
         * Another open tab can still win and notify.
         */
        if (window.location.pathname === orderPath) {
          return;
        }

        title = 'Order verification status changed.';

        body = 'Check order';
        url = orderPath;

        sound = prefsRef.current?.orderVerified || '';
      } else if (idEventType === WSEventTypeIds.OrderToggleProgressStatusByWriter) {
        /*
         * Update every tab.
         */
        refetchOrders();

        const orderData = eventData.data as WSSalesPipelineData;

        title = 'Order progress status changed by writer.';

        body = 'Check order';

        url = `/orders/${orderData.id}`;

        sound = prefsRef.current?.writerWorkSubmitted || '';
      } else if (idEventType === WSEventTypeIds.RevisionStatusChangedByWriter) {
        /*
         * Update every tab.
         */
        refetchOrders();
        refetchRevisions();

        const data = eventData.data as WSSalesPipelineData;

        title = 'Revision progress status changed by writer.';

        body = 'Check revision';

        url = `/revisions/${data.id}`;

        sound = prefsRef.current?.writerWorkSubmitted || '';
      }

      /*
       * We don't have a notification for this event type.
       */
      if (!title) {
        return;
      }

      /*
       * Notifications disabled in user preferences.
       */
      if (typeof prefsRef.current?.enabled === 'boolean' && !prefsRef.current.enabled) {
        return;
      }

      /*
       * Browser permission.
       *
       * At this point all eligible tabs may execute this,
       * but Notification permission is origin-wide.
       */
      const notificationsAllowed = await askNotificationPermission();

      if (!notificationsAllowed) {
        return;
      }

      /*
       * ============================================================
       * CROSS-TAB ELECTION
       * ============================================================
       *
       * All eligible tabs arrive here with exactly the same
       * websocketPayload.
       *
       * Exactly one tab wins.
       */
      await runNotificationOnceAcrossTabs(websocketPayload, () => {
        showNotification({
          title,
          body,
          url,
          sound,

          /*
           * Don't use || here because 0 is a valid volume.
           */
          volume: prefsRef.current?.volume ?? 0.5,
        });
      });
    };

    document.addEventListener('wsMessageReceived', handleNotification as any);

    return () => {
      document.removeEventListener('wsMessageReceived', handleNotification as any);
    };
  }, [refetchOrders, refetchPipelines, refetchRevisions]);
};
